export class CustomError extends Error {
    public description?: string;

    constructor({
        title,
        description
    }: {
        title: string;
        description?: string;
    }) {
        super(title);
        this.description = description;
    }
}

class ErrorManager {
    private errors: Error[] = [];

    private changeListeneres: (() => void)[] = [];
    private retryCallbacks: (() => void)[] = [];

    constructor() {
        window.addEventListener("error", e => {
            this.addError(e.error as Error);
        });
        window.addEventListener("unhandledrejection", e => {
            this.addError(e.reason as Error);
        });
    }

    public addChangeListener = (callback: () => void) => {
        this.changeListeneres.push(callback);
        return () => {
            this.changeListeneres = this.changeListeneres.filter(
                cb => cb !== callback
            );
        };
    };

    public addRetryCallback = (callback: () => void) => {
        this.retryCallbacks.push(callback);
        this.changeListeneres.forEach(callback => {
            callback();
        });
        return () => {
            this.retryCallbacks = this.retryCallbacks.filter(
                cb => cb !== callback
            );
        };
    };

    public getErrors = () => {
        return this.errors;
    };

    public hasRetryCallbacks = () => {
        return this.retryCallbacks.length > 0;
    };

    public addError = (error: Error) => {
        const existing = this.errors.find(e => e === error);
        if (existing) {
            return;
        }
        this.errors = [...this.errors, error]; // immutable change for react propagation
        this.changeListeneres.forEach(callback => {
            callback();
        });
    };

    public clearErrors = () => {
        this.errors = [];
        this.changeListeneres.forEach(callback => {
            callback();
        });
    };

    public retryErrors = () => {
        this.clearErrors();
        this.retryCallbacks.forEach(callback => {
            callback();
        });
    };
}

export const errorManager = new ErrorManager();

export const showCustomError = ({
    title,
    description
}: {
    title: string;
    description?: string;
}) => {
    const error = new CustomError({ title, description });
    errorManager.addError(error);
};

export const showError = (error: Error) => {
    errorManager.addError(error);
};
